import { AdyenPlatformExperience, en_US, fr_FR, nl_NL, TransactionsOverview, TransactionDetails, } from "@adyen/adyen-platform-experience-web";
import api from "@/plugins/api";
import i18n from "@/plugins/i18n";
import { logger } from "./LoggerService";
import { loadAdyenConfig } from "@/shared/utils/configLoader";
import { normalizeLocaleForAdyen } from "@/shared/utils/localeUtils";
import { messages } from "@/shared/i18n";
import { SUPPORTED_TRANSLATIONS } from "@/domain/types";
/**
 * Available Adyen translations array for SDK initialization
 */
const AVAILABLE_ADYEN_TRANSLATIONS = [en_US, nl_NL, fr_FR];
export class AdyenTransactionService {
    constructor() {
        Object.defineProperty(this, "components", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "core", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "transactionMetadataCache", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "merchantIdTtlMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 10 * 60000
        }); // 10 minutes
        Object.defineProperty(this, "lastConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "adyenEnvironment", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
    }
    /**
     * Resolve and validate a locale string to a supported Adyen locale
     * Uses unified normalizeLocale from localeUtils for consistent locale handling
     * @param locale - The locale string to resolve (e.g., "en-US", "fr-FR", "fr", "en")
     * @returns A supported locale (fr-FR, en-US, or nl-NL), defaults to fr-FR
     */
    resolveLocale(locale) {
        return normalizeLocaleForAdyen(locale);
    }
    /**
     * Get the current locale from i18n with fallback
     * @returns The current locale as a supported Adyen locale
     */
    getCurrentLocale() {
        const currentI18nLocale = i18n.global.locale?.value; // todo: use store ?
        return this.resolveLocale(currentI18nLocale);
    }
    /**
     * Load Adyen environment from configuration
     * Caches the value to avoid repeated config loading
     */
    async loadEnvironment() {
        if (this.adyenEnvironment) {
            return this.adyenEnvironment;
        }
        try {
            const config = await loadAdyenConfig();
            const env = config.environment.toLowerCase();
            // Validate and normalize environment value
            if (env === "test" || env === "live") {
                this.adyenEnvironment = env;
                return env;
            }
            else {
                logger.debug(`Invalid Adyen environment '${env}', using default 'test'`);
                this.adyenEnvironment = "test";
                return "test";
            }
        }
        catch (error) {
            logger.debug("Failed to load Adyen environment, using default 'test'", error);
            this.adyenEnvironment = "test";
            return "test";
        }
    }
    /**
     * Creates a session refresh callback for Adyen Platform Experience
     * This callback will be invoked by Adyen SDK when the session expires
     */
    createSessionRefreshCallback() {
        return async () => {
            logger.debug("Adyen SDK requesting new session (refresh or initial load)");
            try {
                const newSession = await this.createSession();
                logger.debug("New session created successfully", newSession?.id);
                return newSession;
            }
            catch (error) {
                logger.debug("Failed to refresh Adyen session", error);
                throw error;
            }
        };
    }
    /**
     * Build Adyen translations from i18n for custom fields
     * Uses direct import of messages to ensure availability even after cache clear
     *
     * CRITICAL: Adyen SDK uses UNDERSCORE format for ALL translation keys
     * - availableTranslations imports: en_US, fr_FR, nl_NL (underscore)
     * - translations object keys: "en_US", "fr_FR", "nl_NL" (ALSO underscore!)
     *
     * @returns Record of translations per locale (with underscore format)
     */
    buildAdyenTranslations() {
        try {
            // Build translations for all supported locales
            const translations = {};
            // Map locale format from "fr-FR" to message key "fr"
            // En attendant de à terme, gérer les locales fr-fr, fr-ca...
            const localeToMessageKey = {
                "fr-FR": "fr",
                "fr-CA": "fr",
                "en-GB": "en",
                "en-US": "en",
                "nl-NL": "nl",
            };
            SUPPORTED_TRANSLATIONS.forEach((locale) => {
                // IMPORTANT: Adyen SDK expects UNDERSCORE format for translation keys!
                // Convert "fr-FR" → "fr_FR" to match availableTranslations format
                const adyenLocaleKey = locale.replace("-", "_"); // fr-FR → fr_FR
                const messageKey = localeToMessageKey[locale];
                if (!messageKey || !messages[messageKey]) {
                    logger.debug(`No messages found for locale: ${locale}`);
                    return;
                }
                // Access translations directly from imported messages
                // This ensures they're always available, even after cache clear
                const localeMessages = messages[messageKey];
                const adyen = localeMessages?.adyen ?? {};
                // All labels are read from Tolgee under `adyen.*`; the inline strings
                // are last-resort fallbacks used only if a key is missing from the
                // bundle (e.g. before a translator has published it).
                translations[adyenLocaleKey] = {
                    "transactions.overview.filters.types.category.label": adyen?.filters?.category?.label ?? "Type",
                    "transactions.overview.filters.types.status.label": adyen?.filters?.status?.label ?? "Status",
                    "transactions.overview.filters.types.currency.label": adyen?.filters?.currency?.label ?? "Currency",
                    "transactions.overview.filters.types.amount.label": adyen?.filters?.amount?.label ?? "Amount",
                    "transactions.overview.filters.label": adyen?.filters?.label ?? "Transaction filters",
                    "common.filters.label": adyen?.common?.filters?.label ?? "Filters",
                    "common.filters.controls.resetAll.label": adyen?.common?.filters?.controls?.resetAll?.label ?? "Clear all",
                    "common.actions.apply.labels.default": adyen?.common?.actions?.apply?.labels?.default ?? "Apply",
                    _paymentPspReference: adyen?.customFields?.paymentPspReference ?? "_paymentPspReference",
                    _merchantId: adyen?.customFields?.merchantId ?? "_merchantId",
                    _originalAmount: adyen?.customFields?.originalAmount ?? "_originalAmount",
                    _transactionId: adyen?.customFields?.transactionId ?? "_transactionId",
                };
            });
            logger.debug("Adyen translations built successfully (with underscore keys):", translations);
            return translations;
        }
        catch (error) {
            logger.debug("Failed to build Adyen translations:", error);
            return {};
        }
    }
    /**
     * Resolve transaction metadata (merchant ID and original amount) for a list of PSP references in a single API call
     * Uses caching to avoid repeated API calls
     */
    async resolveTransactionMetadata(pspReferences) {
        const now = Date.now();
        const uniquePsp = Array.from(new Set((pspReferences || []).filter((v) => v.trim().length > 0).map((v) => v.trim())));
        if (uniquePsp.length === 0) {
            return new Map();
        }
        const result = new Map();
        const missing = [];
        // Check cache first
        for (const key of uniquePsp) {
            const entry = this.transactionMetadataCache.get(key);
            if (entry && entry.expiresAt > now) {
                result.set(key, entry);
            }
            else {
                missing.push(key);
            }
        }
        // Fetch missing data from backend
        if (missing.length > 0) {
            try {
                const response = await api.post(`/transactions/metadata`, missing);
                if (response?.data && typeof response.data === "object") {
                    const fetched = response.data;
                    logger.debug("Transaction metadata API response:", fetched);
                    const expiresAt = now + this.merchantIdTtlMs;
                    // Process fetched data and update cache
                    for (const [psp, entry] of Object.entries(fetched)) {
                        result.set(psp, entry);
                        this.transactionMetadataCache.set(psp, {
                            ...entry,
                            expiresAt,
                        });
                    }
                }
            }
            catch (e) {
                logger.debug("Failed to fetch transaction metadata", e);
            }
        }
        return result;
    }
    /**
     * Create a session for Adyen Platform Experience
     */
    async createSession() {
        try {
            const response = await api.post("/adyen-component/session/transactions");
            // API envelope: { success, errorCode, message, result: { id, token } }
            if (!response.data?.success || !response.data?.result) {
                const message = response.data?.message || "Session creation failed";
                throw new Error(message);
            }
            return {
                id: response.data.result.id,
                token: response.data.result.token,
            };
        }
        catch (error) {
            logger.debug("Error creating transaction session:", error);
            throw error;
        }
    }
    /**
     * Build the configuration for TransactionsOverview component
     * This is factored out to avoid duplication between initialization and locale updates
     */
    buildTransactionsOverviewConfig() {
        return {
            dataCustomization: {
                list: {
                    fields: [
                        { key: "_paymentPspReference", align: "center" },
                        { key: "_transactionId", align: "center" },
                        { key: "_originalAmount", align: "center" },
                    ],
                    onDataRetrieve: async (rows) => {
                        const pspReferences = (rows || [])
                            .map((r) => r?.pspReference ?? r?.paymentPspReference)
                            .filter((v) => typeof v === "string" && v.trim().length > 0)
                            .map((v) => v.trim());
                        const metadata = await this.resolveTransactionMetadata(pspReferences);
                        // Enrich rows
                        const enriched = rows.map((r) => {
                            const psp = (r?.pspReference ?? r?.paymentPspReference);
                            if (!psp) {
                                // Debug logging for missing PSP reference
                                logger.debug("Row missing PSP reference:", {
                                    id: r?.id,
                                    pspReference: r?.pspReference,
                                    paymentPspReference: r?.paymentPspReference,
                                });
                                return {
                                    ...r,
                                    _paymentPspReference: "",
                                    _merchantId: "",
                                    _originalAmount: null,
                                    _transactionId: "",
                                };
                            }
                            const meta = metadata.get(psp);
                            if (!meta) {
                                // Debug logging for missing metadata
                                logger.debug(`No metadata found for PSP ${psp}, row data:`, {
                                    id: r?.id,
                                    pspReference: r?.pspReference,
                                    paymentPspReference: r?.paymentPspReference,
                                });
                                return {
                                    ...r,
                                    _paymentPspReference: psp,
                                    _merchantId: "",
                                    _originalAmount: null,
                                    _transactionId: "",
                                };
                            }
                            return {
                                ...r,
                                _paymentPspReference: psp,
                                _merchantId: meta.merchantId,
                                _originalAmount: meta.originalAmount.toFixed(2),
                                _transactionId: meta.transactionId,
                            };
                        });
                        return enriched;
                    },
                },
                details: {
                    fields: [
                        { key: "_merchantId", align: "left" },
                        { key: "_transactionId", align: "left" },
                    ],
                    onDataRetrieve: async (data) => {
                        try {
                            const candidates = [];
                            const tryPush = (v) => {
                                if (typeof v === "string" && v.trim().length > 0) {
                                    candidates.push(v.trim());
                                }
                            };
                            tryPush(data?.pspReference);
                            tryPush(data?.paymentPspReference);
                            if (Array.isArray(data?.payments)) {
                                for (const p of data.payments) {
                                    tryPush(p?.pspReference ?? p?.paymentPspReference);
                                }
                            }
                            const metadata = await this.resolveTransactionMetadata(candidates);
                            const psp = candidates[0];
                            const meta = psp ? metadata.get(psp) : undefined;
                            const merchantId = meta?.merchantId ?? "";
                            const transactionId = meta?.transactionId ?? "";
                            return {
                                ...data,
                                _merchantId: merchantId,
                                _transactionId: transactionId,
                            };
                        }
                        catch (e) {
                            logger.debug("Transaction Details onDataRetrieve failed", e);
                            return data;
                        }
                    },
                },
            },
            onRecordSelection: (record) => {
                const selectedId = record?.id || record?.transactionId || record;
                if (selectedId) {
                    // Prefer callback provided by caller (TransactionUseCases) if available
                    if (typeof this.lastConfig?.onRecordSelection === "function") {
                        try {
                            this.lastConfig.onRecordSelection(String(selectedId));
                        }
                        catch (error) {
                            logger.debug("Error calling onRecordSelection callback:", error);
                        }
                    }
                    else {
                        this.handleTransactionSelection(String(selectedId));
                    }
                }
            },
            onError: (err) => {
                logger.debug("TransactionsOverview error:", err);
            },
        };
    }
    /**
     * Initialize Adyen Platform Experience components
     */
    async initializeTransactionComponents(config) {
        try {
            logger.debug("Initializing Adyen Platform Experience...");
            // Store config for potential reinitializations
            this.lastConfig = config;
            // Validate initial session first - if it fails, don't initialize Adyen SDK at all
            // Error will be caught by TransactionUseCases and navigate to /error page
            const initialSession = await config.onSessionCreate();
            if (!initialSession?.id || !initialSession?.token) {
                throw new Error("Failed to create valid session - missing credentials");
            }
            logger.debug("Initial session created successfully", initialSession.id);
            // Get current locale with fallback
            const resolvedLocale = this.getCurrentLocale();
            const translations = this.buildAdyenTranslations();
            // Load Adyen environment configuration
            const environment = await this.loadEnvironment();
            // Session is valid, now initialize Adyen SDK
            // IMPORTANT: Pass the session refresh callback to allow Adyen to automatically refresh
            // the session when it expires
            const core = await AdyenPlatformExperience({
                environment,
                availableTranslations: AVAILABLE_ADYEN_TRANSLATIONS,
                translations,
                locale: resolvedLocale,
                onSessionCreate: this.createSessionRefreshCallback(),
            });
            this.core = core;
            // Create TransactionsOverview with factored configuration
            const transactionsOverview = new TransactionsOverview({
                core,
                ...this.buildTransactionsOverviewConfig(),
            });
            this.components = {
                transactionsOverview,
            };
            logger.debug("Transaction components initialized successfully");
            return this.components;
        }
        catch (error) {
            logger.debug("Error initializing transaction components:", error);
            throw error;
        }
    }
    // Au clic sur une ligne: délègue au composant Vue via un événement global
    handleTransactionSelection(transactionId) {
        try {
            window.dispatchEvent(new CustomEvent("opay:transaction-selected", {
                detail: { transactionId },
            }));
        }
        catch (error) {
            logger.debug("Error handling transaction selection:", error);
        }
    }
    // Suppression du dialog interne: le composant Vue gère la popup et le container
    /**
     * Initialize Transaction Details component
     * Opens a popup with transaction details using a hardcoded PSP ID
     */
    async initializeTransactionDetails(containerSelector, adyenTransactionId) {
        try {
            if (!this.core) {
                // Initialize core if not already done
                const resolvedLocale = this.getCurrentLocale();
                // Validate initial session first - if it fails, don't initialize Adyen SDK
                // Error will be caught by TransactionUseCases and navigate to /error
                const initialSession = await this.createSession();
                if (!initialSession?.id || !initialSession?.token) {
                    throw new Error("Failed to create valid session for transaction details");
                }
                logger.debug("Initial session for details created successfully", initialSession.id);
                // Load Adyen environment configuration
                const environment = await this.loadEnvironment();
                // Session is valid, now initialize Adyen SDK
                // IMPORTANT: Pass the session refresh callback to allow automatic session refresh
                this.core = await AdyenPlatformExperience({
                    environment,
                    availableTranslations: AVAILABLE_ADYEN_TRANSLATIONS,
                    translations: this.buildAdyenTranslations(),
                    locale: resolvedLocale,
                    onSessionCreate: this.createSessionRefreshCallback(),
                });
            }
            // Ensure container exists and is clean (avoid duplicate mount)
            const container = document.querySelector(containerSelector);
            if (!container) {
                throw new Error(`Container not found: ${containerSelector}`);
            }
            container.innerHTML = "";
            const transactionDetails = new TransactionDetails({
                core: this.core,
                id: adyenTransactionId,
                dataCustomization: {
                    details: {
                        fields: [
                            { key: "_merchantId", align: "left" },
                            { key: "_transactionId", align: "left" },
                        ],
                        onDataRetrieve: async (data) => {
                            try {
                                const psp = (data?.pspReference ?? data?.paymentPspReference);
                                const metadata = await this.resolveTransactionMetadata(psp ? [psp] : []);
                                const meta = psp ? metadata.get(psp) : undefined;
                                const merchantId = meta?.merchantId ?? "";
                                const transactionId = meta?.transactionId ?? "";
                                return {
                                    ...data,
                                    _merchantId: merchantId,
                                    _transactionId: transactionId,
                                };
                            }
                            catch (e) {
                                logger.debug("Transaction Details onDataRetrieve failed", e);
                                return data;
                            }
                        },
                    },
                },
            });
            // Mount the component to the container
            transactionDetails.mount(containerSelector);
        }
        catch (error) {
            logger.debug("Error initializing Transaction Details :", error);
            throw error;
        }
    }
    /**
     * Monter l'overview
     */
    mountComponents(components, overviewSelector) {
        try {
            const container = document.querySelector(overviewSelector);
            if (!container) {
                logger.debug(`Container ${overviewSelector} not found, skipping mount (likely navigated away)`);
                return;
            }
            components.transactionsOverview.mount(overviewSelector);
        }
        catch (error) {
            logger.debug("Error mounting transaction components:", error);
            throw error;
        }
    }
    /**
     * Update the locale of the Adyen Platform Experience
     * This will reinitialize the components with the new locale
     * Note: Adyen Platform Experience does not support dynamic locale changes,
     * so we need to fully reinitialize the core and components
     */
    async updateLocale(newLocale, overviewSelector = "#transactions-overview-container") {
        try {
            // Resolve and validate the new locale
            const resolvedLocale = this.resolveLocale(newLocale);
            logger.debug(`Updating Adyen locale to: ${resolvedLocale}`);
            // Adyen Platform Experience does not support setLocale()
            // We need to cleanup and reinitialize with the new locale
            if (!this.core || !this.lastConfig) {
                logger.debug("Cannot update locale: core not initialized or config missing");
                return;
            }
            // Store the current state to reinitialize
            const wasInitialized = this.components !== null;
            if (!wasInitialized) {
                logger.debug("Components not mounted, skipping locale update");
                return;
            }
            logger.debug("Reinitializing Adyen components with new locale...");
            // Cleanup existing components and core
            this.cleanup();
            // Reinitialize with new locale
            const translations = this.buildAdyenTranslations();
            // Load Adyen environment configuration
            const environment = await this.loadEnvironment();
            this.core = await AdyenPlatformExperience({
                environment,
                availableTranslations: AVAILABLE_ADYEN_TRANSLATIONS,
                translations,
                locale: resolvedLocale,
                onSessionCreate: this.createSessionRefreshCallback(),
            });
            logger.debug("Adyen core reinitialized with new locale, recreating components...");
            // Recreate TransactionsOverview component with factored configuration
            const transactionsOverview = new TransactionsOverview({
                core: this.core,
                ...this.buildTransactionsOverviewConfig(),
            });
            this.components = {
                transactionsOverview,
            };
            // Remount the component
            const container = document.querySelector(overviewSelector);
            if (container) {
                transactionsOverview.mount(overviewSelector);
            }
            else {
                logger.debug(`Container ${overviewSelector} not found during locale update, skipping mount`);
            }
            logger.debug("Adyen components successfully reinitialized and mounted with new locale");
        }
        catch (error) {
            logger.debug("Error updating Adyen locale:", error);
            throw error;
        }
    }
    /**
     * Cleanup resources when component is destroyed
     */
    cleanup() {
        try {
            if (this.components) {
                if (this.components.transactionsOverview.unmount) {
                    this.components.transactionsOverview.unmount();
                }
                this.components = null;
                this.core = null;
                logger.debug("Transaction components cleaned up successfully");
            }
        }
        catch (error) {
            logger.debug("Error during cleanup:", error);
        }
    }
}
